/**
 * Shared module definitions used by both the Superadmin AccountForm
 * (to render module toggles) and AppSidebar (to filter nav items).
 *
 * Each module `key` must match the sidebar NavItem `titleKey`.
 */

export interface ModuleDef {
  key: string;
  label: string;
  section: "main" | "administration" | "teamManagement" | "bottom";
  alwaysOn?: boolean;
}

export const ALL_MODULES: ModuleDef[] = [
  // ── Main ──────────────────────────────────────────────────
  { key: "dashboard",         label: "Dashboard",           section: "main", alwaysOn: true },
  { key: "myProfile",         label: "My Profile",          section: "main" },
  { key: "peopleDirectory",   label: "People Directory",    section: "main" },
  { key: "leaveManagement",   label: "Leave Management",    section: "main" },
  { key: "timekeeping",       label: "Timekeeping",         section: "main" },
  { key: "performance",       label: "Performance",         section: "main" },
  { key: "salary",            label: "My Salary",           section: "main" },
  { key: "myExpenses",        label: "My Expenses",         section: "main" },
  { key: "careerDevelopment", label: "Career Development",  section: "main" },
  { key: "documents",         label: "Documents",           section: "main" },
  { key: "myOnboarding",      label: "My Onboarding",       section: "main" },
  { key: "mySurveys",         label: "My Surveys",          section: "main" },
  { key: "raiseConcern",      label: "Raise a Concern",     section: "main" },

  // ── Administration ────────────────────────────────────────
  { key: "organization",        label: "Organization",         section: "administration" },
  { key: "teamLeave",           label: "Leave Management",     section: "administration" },
  { key: "recruitment",         label: "Recruitment",          section: "administration" },
  { key: "payroll",             label: "Payroll",              section: "administration" },
  { key: "equipmentManagement", label: "Equipment Management", section: "administration" },
  { key: "medicalCheckups",     label: "Medical Checkups",     section: "administration" },
  { key: "expenses",            label: "Expense Management",   section: "administration" },
  { key: "onboarding",          label: "Workforce Lifecycle",  section: "administration" },
  { key: "surveys",             label: "Surveys",              section: "administration" },
  { key: "fieldPermissions",    label: "Field Permissions",    section: "administration" },
  { key: "navPermissions",      label: "Nav Permissions",      section: "administration" },
  { key: "auditLog",            label: "Audit Log",            section: "administration" },
  { key: "workflows",           label: "Workflows",            section: "administration" },
  { key: "hrAnalytics",         label: "HR Analytics",         section: "administration" },
  { key: "aiAssistant",         label: "AI Assistant",             section: "administration" },
  { key: "certifications",      label: "Certifications & Training", section: "administration" },
  { key: "hrOpenCases",         label: "HR Open Cases",             section: "administration" },

  // ── Team Management ───────────────────────────────────────
  { key: "teamOverview",     label: "Team Overview",     section: "teamManagement" },

  // ── Bottom ────────────────────────────────────────────────
  { key: "notifications", label: "Notifications", section: "bottom", alwaysOn: true },
  { key: "settings",      label: "Settings",      section: "bottom", alwaysOn: true },
];

export const ALWAYS_ON_MODULES = new Set(
  ALL_MODULES.filter((m) => m.alwaysOn).map((m) => m.key),
);

export function isModuleEnabled(modules: Record<string, boolean> | null | undefined, key: string): boolean {
  return ALWAYS_ON_MODULES.has(key) || modules?.[key] !== false;
}

export function isNavigationVisible(
  permissions: import("./api").NavVisibilitySetting[],
  user: { role: { mainRole: string; subRole?: string }; isLineManager?: boolean } | null | undefined,
  key: string,
): boolean {
  if (!user) return false;
  const permission = permissions.find(p => p.navKey === key);
  if (!permission) return true;
  if (user.role.mainRole === "admin") return permission.visibleToAdmin;
  if (user.role.mainRole === "hr") return permission.visibleToHr;
  if (user.role.subRole === "c_level") return permission.visibleToCLevel;
  if (user.isLineManager || user.role.subRole === "line_manager") return permission.visibleToLineManager;
  return permission.visibleToUser;
}

/** Section labels for grouping in the UI */
export const MODULE_SECTIONS: Record<ModuleDef["section"], string> = {
  main: "Main",
  administration: "Administration",
  teamManagement: "Team Management",
  bottom: "System",
};
